/**
 * 기본 수수료율 설정 — 레거시 `baseFeeRateSetting.js` 변환 헬퍼.
 * UI 구간 상한은 서버 rangeMax + 1 (미만 표기)로 다룬다.
 */
import type {
  FixedFeeItem,
  RangeFeeItem,
} from '@/entities/fee-rate/api/fee-rate.api';

export type FixedFeeUserUi = {
  key: string;
  companyName: string;
  account: string;
};

export type RangeFeeRowUi = {
  key: string;
  id?: number;
  minAmount: number;
  maxAmount: number;
  rate: number;
};

export type FixedFeeRowUi = {
  key: string;
  id?: number;
  rate: number;
  users: FixedFeeUserUi[];
};

export const createRangeRows = (): RangeFeeRowUi[] => [
  {
    key: `r-${Date.now()}-1`,
    minAmount: 0,
    maxAmount: 1_000_000,
    rate: 0,
  },
];

export const createFixedRows = (): FixedFeeRowUi[] => [
  {
    key: `f-${Date.now()}-1`,
    rate: 0,
    users: [],
  },
];

export const toUiRangeMax = (value: number | null | undefined): number =>
  Number(value ?? 0) + 1;

export const toServerRangeMax = (value: number | null | undefined): number =>
  Math.max(0, Number(value || 0) - 1);

export const toRangeRows = (rangeFees: RangeFeeItem[] = []): RangeFeeRowUi[] => {
  if (!Array.isArray(rangeFees) || rangeFees.length === 0) {
    return createRangeRows();
  }
  return rangeFees.map((fee, index) => ({
    key: `r-${fee.id ?? index}`,
    id: fee.id,
    minAmount: Number(fee.rangeMin ?? 0),
    maxAmount: toUiRangeMax(fee.rangeMax),
    rate: Number(fee.rate ?? 0),
  }));
};

export const toFixedRows = (fixedFees: FixedFeeItem[] = []): FixedFeeRowUi[] => {
  if (!Array.isArray(fixedFees) || fixedFees.length === 0) {
    return createFixedRows();
  }
  return fixedFees.map((fee, index) => ({
    key: `f-${fee.id ?? index}`,
    id: fee.id,
    rate: Number(fee.rate ?? 0),
    users: Array.isArray(fee.users)
      ? fee.users.map((user) => {
          const userKey = String(user.userId);
          return {
            key: userKey,
            companyName: user.companyName || `사용자 ${userKey}`,
            account: user.email || `ID: ${userKey}`,
          };
        })
      : Array.isArray(fee.userIds)
        ? fee.userIds.map((userId) => {
            const userKey = String(userId);
            return {
              key: userKey,
              companyName: `사용자 ${userKey}`,
              account: `ID: ${userKey}`,
            };
          })
        : [],
  }));
};

/** 2행부터 minAmount를 직전 행 maxAmount로 맞춤 */
export const normalizeRangeMins = (
  rows: RangeFeeRowUi[] = [],
): RangeFeeRowUi[] => {
  if (!Array.isArray(rows) || rows.length === 0) return createRangeRows();
  return rows.map((row, index, list) =>
    index === 0
      ? row
      : {
          ...row,
          minAmount: Number(list[index - 1]?.maxAmount || 0),
        },
  );
};

export const handleRateStep = (value: number | null | undefined, delta: number) =>
  Number(Math.min(100, (value || 0) + delta).toFixed(2));

/** YYYYMM 전월 (1월 → 전년 12월) */
export const getPreviousRefYm = (refYm: string): string => {
  if (!refYm || refYm.length !== 6) return '';
  const y = Number(refYm.slice(0, 4));
  const m = Number(refYm.slice(4, 6));
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return '';
  if (m === 1) return `${y - 1}12`;
  return `${y}${String(m - 1).padStart(2, '0')}`;
};

export const buildBasicFeePolicyPayload = (
  settlementYearMonth: string,
  rangeRows: RangeFeeRowUi[],
  fixedRows: FixedFeeRowUi[],
) => ({
  settlementYearMonth,
  rangeFees: rangeRows.map((row) => ({
    ...(row.id != null ? { id: row.id } : {}),
    rangeMin: Number(row.minAmount || 0),
    rangeMax: toServerRangeMax(row.maxAmount),
    rate: Number(row.rate || 0),
  })),
  fixedFees: fixedRows.map((row) => ({
    ...(row.id != null ? { id: row.id } : {}),
    rate: Number(row.rate || 0),
    userIds: row.users
      .map((user) => Number(user.key))
      .filter((id) => Number.isFinite(id)),
  })),
  policyConfigValid: true,
});

export const formatAmountInput = (value: number | string): string =>
  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const parseAmountInput = (raw: string): number => {
  const digits = raw.replace(/[^\d]/g, '');
  return digits ? Number(digits) : 0;
};
